import {createPath} from 'react-router-dom';
import type {Path} from 'react-router-dom';

export const authRedirectParam = 'next';

export function getAuthRedirectFromSearch(search: string): string | null {
  const redirectUri = new URLSearchParams(search).get(authRedirectParam);
  return isSafeAuthRedirectUri(redirectUri) ? redirectUri : null;
}

export function getAuthPagePathWithRedirect(
  authPagePath: string,
  location: Partial<Path>,
): string {
  return getAuthPagePathWithRedirectUri(authPagePath, createPath(location));
}

export function getAuthPagePathWithRedirectUri(
  authPagePath: string,
  redirectUri: string,
): string {
  const params = new URLSearchParams();
  params.set(authRedirectParam, redirectUri);
  return `${authPagePath}?${params}`;
}

export function getAuthPagePathPreservingRedirect(
  authPagePath: string,
  location: Partial<Path>,
): string {
  const redirectUri =
    getAuthRedirectFromSearch(location.search ?? '') ?? createPath(location);
  const params = new URLSearchParams();
  params.set(authRedirectParam, redirectUri);
  return `${authPagePath}?${params}`;
}

/**
 * Only a path on this site may be returned to after signing in. Anything a
 * browser would read as another site is refused: "//host", "/\host" (a
 * backslash is read as a slash) and "/<tab>/host" (control characters are
 * dropped before the address is read).
 */
function isSafeAuthRedirectUri(
  redirectUri: string | null,
): redirectUri is string {
  if (
    !redirectUri ||
    !redirectUri.startsWith('/') ||
    /^\/[\/\\]/.test(redirectUri) ||
    // eslint-disable-next-line no-control-regex
    /[\s\u0000-\u001f\u007f]/.test(redirectUri)
  ) {
    return false;
  }
  try {
    // what the browser would make of it: the same origin, or refused
    const base = 'https://this-site.invalid';
    const url = new URL(redirectUri, base);
    return url.origin === base && url.pathname.startsWith('/');
  } catch (e) {
    return false;
  }
}
